import classNames from 'classnames';
import React, { useCallback, useState } from 'react';
import { RadioButton as TPARadioButton, TextField as TPATextField } from 'wix-ui-tpa/cssVars';
import { FIELD_TYPES } from '../../constants';
import { ForcedState, useForcedState } from '../../contexts/forced-state-context';
import { useTranslation } from '../../contexts/translation-context';
import { FIELD_ERROR_CLASS, useError } from '../../field-error';
import { OTHER_ID, getIsOtherOptionSelected, getOtherLabel, getOtherValue } from '../../fields/radio-group/radio-group-utils';
import { useFieldAttributes } from '../../services/field-attributes';
import { FieldDescriptionWUT } from '../field-description';
import { FieldLabelWUT } from '../field-label';
import { FieldErrorWUT } from '../field-error';
import Fieldset from '../fieldset';
import { useTpaStyleParams } from '../use-tpa-style-params';
import styles from './radio-group.scss';
import { classes } from './radio-group.st.css';
export const RadioGroupComponent = _ref => {
  let {
    id,
    target,
    label,
    hideLabel = false,
    options,
    description,
    addOther,
    addOtherLabel,
    addOtherPlaceholder,
    required,
    value,
    onChange,
    onFocus,
    onBlur,
    error,
    customErrors,
    disabled = false,
    fieldType = FIELD_TYPES.RADIO_GROUP,
    otherInputCharacterLimit,
    additionalInfo,
    FieldLayout
  } = _ref;
  const {
    labelId,
    inputId,
    errorId,
    descriptionId,
    ariaDescribedBy
  } = useFieldAttributes(id);
  const {
    hasError,
    errorMessage
  } = useError(error, customErrors, fieldType);
  const isOtherOptionSelected = getIsOtherOptionSelected(options, value);
  const [otherSelected, setOtherSelected] = useState(isOtherOptionSelected);
  const [otherValue, setOtherValue] = useState(isOtherOptionSelected && value ? getOtherValue(value, addOtherLabel) : '');
  const {
    t
  } = useTranslation();
  const otherLabel = getOtherLabel(addOtherLabel, t);
  const forcedHover = useForcedState().includes(ForcedState.INPUT_FIELDS_HOVER);
  const handleChange = useCallback(option => {
    onChange(option);
  }, [onChange]);
  const handleOtherChange = event => {
    event.stopPropagation();
    const input = event.target.value;
    let _otherValue;
    if (input) {
      _otherValue = `${addOtherLabel}: ${input}`;
    } else {
      _otherValue = otherLabel;
    }
    handleChange(_otherValue);
    setOtherValue(input);
    setOtherSelected(true);
  };
  const {
    inputTheme,
    isBorderStyleNone
  } = useTpaStyleParams();
  return /*#__PURE__*/React.createElement(FieldLayout, {
    fieldId: id,
    renderLabel: () => /*#__PURE__*/React.createElement(FieldLabelWUT, {
      labelId: labelId,
      required: required,
      label: label,
      hideLabel: hideLabel
    }),
    renderInput: () => /*#__PURE__*/React.createElement(Fieldset, {
      id: inputId,
      ariaLabelledBy: labelId,
      className: classNames(classes.radioGroup, {
        [FIELD_ERROR_CLASS]: hasError
      }),
      ariaDescribedBy: ariaDescribedBy,
      onFocus: onFocus,
      onBlur: onBlur
    }, options == null ? void 0 : options.map(_ref2 => {
      let {
        label: optionLabel,
        value: optionValue
      } = _ref2;
      const valueToSave = optionValue ?? optionLabel;
      return /*#__PURE__*/React.createElement(TPARadioButton, {
        key: `radioButton-${optionLabel}`,
        label: optionLabel,
        checked: valueToSave === value && Boolean(valueToSave),
        value:
        // TODO remove when WSR fixes bug
        valueToSave ? valueToSave : undefined,
        onChange: e => {
          const newValue = String(e.value);
          if (newValue !== value) {
            handleChange(newValue);
            setOtherSelected(false);
            setOtherValue('');
          }
        },
        name: label,
        disabled: disabled,
        className: classNames(classes.radioButton, styles.radioButton, {
          [styles.radioButtonError]: hasError,
          [classes.hover]: forcedHover
        })
      });
    }), addOther && /*#__PURE__*/React.createElement("div", {
      className: classes.otherContainer
    }, /*#__PURE__*/React.createElement(TPARadioButton, {
      key: `radioButton-${addOtherLabel || OTHER_ID}`,
      label: otherLabel,
      checked: otherSelected,
      value: addOtherLabel || OTHER_ID,
      onChange: () => {
        handleChange(`${otherLabel}`);
        setOtherSelected(true);
      },
      disabled: disabled,
      name: label,
      error: hasError,
      className: classNames(classes.radioButton, styles.radioButton, {
        [styles.radioButtonError]: hasError
      })
    }), otherSelected && /*#__PURE__*/React.createElement(TPATextField, {
      className: classNames(classes.otherInput, {
        [classes.noBorder]: isBorderStyleNone
      }),
      value: otherValue,
      placeholder: addOtherPlaceholder,
      onChange: handleOtherChange,
      disabled: disabled,
      error: hasError,
      theme: inputTheme,
      maxLength: otherInputCharacterLimit,
      showCharCount: Boolean(otherInputCharacterLimit)
    }))),
    renderDescription: () => /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(FieldErrorWUT, {
      target: target,
      hasError: hasError,
      errorMessage: errorMessage,
      id: errorId
    }), /*#__PURE__*/React.createElement(FieldDescriptionWUT, {
      description: description,
      id: descriptionId
    }), additionalInfo)
  });
};
//# sourceMappingURL=radio-group-component.js.map